import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { CharCounter } from '@/components/shared/CharCounter';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';
import type { FaqEntry } from '@/services/kbProfileService';

const MAX_FAQ = CAPS.faq_items;

interface FaqEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialFaq: FaqEntry[];
}

export function FaqEditor({ open, onOpenChange, initialFaq }: FaqEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile } = useOnboardingState();
  const [entries, setEntries] = useState<FaqEntry[]>(
    initialFaq.length > 0 ? [...initialFaq] : [{ question: '', answer: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  function updateEntry(index: number, field: keyof FaqEntry, value: string) {
    setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
  }

  function addEntry() {
    if (entries.length < MAX_FAQ) {
      setEntries((prev) => [...prev, { question: '', answer: '' }]);
    }
  }

  function removeEntry(index: number) {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      const valid = entries.filter((e) => e.question.trim() || e.answer.trim());
      await updateKbProfile({ faq: valid });
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  // The save payload filters out blank entries, so the backend's list index
  // (what a 422 path like `faq.1.answer` refers to) can differ from the UI
  // index — map UI position → payload position before looking up the error.
  function payloadIndex(uiIndex: number): number {
    return (
      entries.slice(0, uiIndex + 1).filter((e) => e.question.trim() || e.answer.trim()).length - 1
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.faq.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.faq.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[min(60dvh,calc(100dvh-16rem))] overflow-y-auto pr-1">
          {entries.map((entry, idx) => (
            <div key={idx} className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('bot.enrich.faq.entry_label', { n: idx + 1 })}
                </span>
                {entries.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('bot.enrich.faq.remove')}
                    className={cn('h-7 w-7', HIT_AREA_44)}
                    onClick={() => removeEntry(idx)}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`faq-q-${idx}`}>{t('bot.enrich.faq.question_label')}</Label>
                <Input
                  id={`faq-q-${idx}`}
                  value={entry.question}
                  onChange={(e) => updateEntry(idx, 'question', e.target.value)}
                  placeholder={t('bot.enrich.faq.question_placeholder')}
                  maxLength={CAPS.faq_question}
                  className="min-h-11"
                />
                <CharCounter value={entry.question} max={CAPS.faq_question} mode="near-limit" />
                <FieldError errors={fieldErrors} path={`faq.${payloadIndex(idx)}.question`} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`faq-a-${idx}`}>{t('bot.enrich.faq.answer_label')}</Label>
                <Textarea
                  id={`faq-a-${idx}`}
                  value={entry.answer}
                  onChange={(e) => updateEntry(idx, 'answer', e.target.value)}
                  placeholder={t('bot.enrich.faq.answer_placeholder')}
                  rows={2}
                  maxLength={CAPS.faq_answer}
                />
                <CharCounter value={entry.answer} max={CAPS.faq_answer} />
                <FieldError errors={fieldErrors} path={`faq.${payloadIndex(idx)}.answer`} />
              </div>
            </div>
          ))}
          {entries.length < MAX_FAQ && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addEntry}
              className="min-h-11 w-full"
            >
              <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
              {t('bot.enrich.faq.add')}
            </Button>
          )}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
